// (c) chaehongee. Confidential and proprietary.

import { http, buildApiUrl } from './http'
import { analytics } from './analytics'
import type {
    CanvasPositionsResponse,
    CanvasPositionsUpdate,
    SessionResponse,
    SessionInfoResponse,
    SessionListResponse,
    SessionUpdate,
    SessionShareRequest,
    SessionUnshareRequest,
    SessionCollaboratorsResponse,
} from 'models-ts/session'

export async function createSession(): Promise<SessionResponse> {
    const session = await http<SessionResponse>(buildApiUrl('/session/'), { method: 'POST' })

    // Track session created event
    analytics.track('session_created', {
        session_id: session.id,
    })

    return session
}

export async function getSessionInfo(sessionId: string): Promise<SessionInfoResponse> {
    const session = await http<SessionInfoResponse>(buildApiUrl(`/session/${sessionId}`), {
        method: 'GET',
    })

    // Track session opened event
    analytics.track('session_opened', {
        session_id: sessionId,
        node_count: session.nodes?.length || 0,
    })

    return session
}

export async function listSessions(
    page: number,
    pageSize: number,
    scope: 'my' | 'public' | 'all' = 'public'
): Promise<SessionListResponse> {
    const params = new URLSearchParams({
        page: String(page),
        page_size: String(pageSize),
        scope
    })
    return http<SessionListResponse>(buildApiUrl(`/session/?${params.toString()}`), {
        method: 'GET',
    })
}

export async function updateSession(sessionId: string, updates: SessionUpdate): Promise<SessionResponse> {
    return http<SessionResponse>(buildApiUrl(`/session/${sessionId}`), {
        method: 'PATCH',
        body: JSON.stringify(updates),
        headers: { 'Content-Type': 'application/json' },
    })
}

export async function updateCanvasPositions(
    sessionId: string,
    updates: CanvasPositionsUpdate
): Promise<CanvasPositionsResponse> {
    return http<CanvasPositionsResponse>(buildApiUrl(`/session/${sessionId}/canvas-positions`), {
        method: 'PUT',
        body: JSON.stringify(updates),
        headers: { 'Content-Type': 'application/json' },
    })
}

export async function shareSessionByEmail(sessionId: string, email: string): Promise<SessionResponse> {
    const request: SessionShareRequest = { email }
    return http<SessionResponse>(buildApiUrl(`/session/${sessionId}/share`), {
        method: 'POST',
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' },
    })
}

export async function unshareSession(sessionId: string, userId: string): Promise<SessionResponse> {
    const request: SessionUnshareRequest = { user_id: userId }
    return http<SessionResponse>(buildApiUrl(`/session/${sessionId}/unshare`), {
        method: 'POST',
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' },
    })
}

export async function getSessionCollaborators(sessionId: string): Promise<SessionCollaboratorsResponse> {
    return http<SessionCollaboratorsResponse>(
        buildApiUrl(`/session/${sessionId}/collaborators`),
        { method: 'GET' }
    )
}

export async function inviteSessionCollaborator(
    sessionId: string,
    email: string
): Promise<SessionCollaboratorsResponse> {
    const request: SessionShareRequest = { email }
    return http<SessionCollaboratorsResponse>(buildApiUrl(`/session/${sessionId}/invite`), {
        method: 'POST',
        body: JSON.stringify(request),
        headers: { 'Content-Type': 'application/json' },
    })
}

export async function duplicateSession(sessionId: string): Promise<SessionResponse> {
    const session = await http<SessionResponse>(
        buildApiUrl(`/session/${sessionId}/duplicate`),
        { method: 'POST' }
    )

    analytics.track('session_duplicated', {
        source_session_id: sessionId,
        new_session_id: session.id,
    })

    return session
}

export async function revokeSessionInvite(
    sessionId: string,
    inviteId: string
): Promise<SessionCollaboratorsResponse> {
    return http<SessionCollaboratorsResponse>(
        buildApiUrl(`/session/${sessionId}/invite/${inviteId}/revoke`),
        { method: 'POST' }
    )
}
